import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, TextField, Button } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../Snackbar';
import { registerStaff, loginStaff } from '../../api/userApi';
import { useAuth } from '../../context/AuthContext';


export default function AuthTabs() {
  const [tab, setTab] = useState(0);
  const { showSnackbar } = useSnackbar();
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleTabChange = (_, newValue) => setTab(newValue);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/patient', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Login formik
  const loginFormik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: Yup.object({
      email: Yup.string().email().required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        const res = await loginStaff(values);
        if (res.data.succeeded) {
          login(res.data.data.token, res.data.data);
          showSnackbar(res.data.messages[0], 'success');
          navigate('/patient');
        }
        else {
          showSnackbar(res.data.messages[0], 'error');
        }
      } catch (err) {
        showSnackbar(err.response?.data?.message || 'Login failed', 'error');
      }
    },
  });

  // Register formik
  const registerFormik = useFormik({
    initialValues: { name: '', email: '', password: '', confirmPassword: '' },
    validationSchema: Yup.object({
      name: Yup.string().required('Name is required'),
      email: Yup.string().email('Invalid email').required('Email is required'),
      password: Yup.string()
        .required('Password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required')
    }),
    onSubmit: async (values) => {
      try {
        const res = await registerStaff({
          name: values.name,
          email: values.email,
          password: values.password
        });
        if (res.data.succeeded) {
          showSnackbar(res.data.messages[0], 'success');
          navigate(`/otp-verify?i=${btoa(values.email)}`);
        }
        else {
          showSnackbar(res.data.messages[0], 'error');
        }
      } catch (err) {
        showSnackbar(err.response?.data?.message || 'Registration failed', 'error');
      }
    },
  });

  return (
    <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
      <Tabs value={tab} onChange={handleTabChange} centered>
        <Tab label="Login" />
        <Tab label="Register" />
      </Tabs>

      {tab === 0 && (
        <form onSubmit={loginFormik.handleSubmit}>
          <TextField fullWidth label="Email" {...loginFormik.getFieldProps('email')} error={loginFormik.touched.email && Boolean(loginFormik.errors.email)} helperText={loginFormik.touched.email && loginFormik.errors.email} margin="normal" />
          <TextField fullWidth type="password" label="Password" {...loginFormik.getFieldProps('password')} error={loginFormik.touched.password && Boolean(loginFormik.errors.password)} helperText={loginFormik.touched.password && loginFormik.errors.password} margin="normal" />
           <Box display="flex" justifyContent="flex-end" mt={1}>
            <Button
              onClick={() => navigate('/forgot-password')}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Forgot Password?
            </Button>
          </Box>
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>Login</Button>
        </form>
      )}

      {tab === 1 && (
        <form onSubmit={registerFormik.handleSubmit}>
          <TextField fullWidth label="Name" {...registerFormik.getFieldProps('name')} error={registerFormik.touched.name && Boolean(registerFormik.errors.name)} helperText={registerFormik.touched.name && registerFormik.errors.name} margin="normal" />
          <TextField fullWidth label="Email" {...registerFormik.getFieldProps('email')} error={registerFormik.touched.email && Boolean(registerFormik.errors.email)} helperText={registerFormik.touched.email && registerFormik.errors.email} margin="normal" />
          <TextField fullWidth type="password" label="Password" {...registerFormik.getFieldProps('password')} error={registerFormik.touched.password && Boolean(registerFormik.errors.password)} helperText={registerFormik.touched.password && registerFormik.errors.password} margin="normal" />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            {...registerFormik.getFieldProps('confirmPassword')}
            error={registerFormik.touched.confirmPassword && Boolean(registerFormik.errors.confirmPassword)}
            helperText={registerFormik.touched.confirmPassword && registerFormik.errors.confirmPassword}
            margin="normal"
          />
          <Button type="submit" variant="contained" fullWidth sx={{ mt: 2 }}>Register</Button>
        </form>
      )}
    </Box>
  );
}
